import {
    Component,
    ElementRef,
    EventEmitter,
    forwardRef,
    HostListener,
    Input,
    OnDestroy,
    OnInit,
    Output,
    QueryList,
    ViewChild,
    ViewChildren,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { ConnectedPosition, Overlay, ScrollStrategy } from '@angular/cdk/overlay';
import { Observable, Subject, of } from 'rxjs';
import {
    catchError,
    debounceTime,
    distinctUntilChanged,
    finalize,
    switchMap,
    takeUntil,
} from 'rxjs/operators';

@Component({
    selector: 'core-ui-autocomplete-form',
    templateUrl: './input-autocomplete-form.component.html',
    styleUrls: ['./input-autocomplete-form.component.scss'],
    standalone: false,
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => AutocompleteFormComponent),
            multi: true,
        },
    ],
})
export class AutocompleteFormComponent<T> implements ControlValueAccessor, OnInit, OnDestroy {
    private static nextId = 0;

    @ViewChild('originRef', { read: ElementRef }) originRef?: ElementRef<HTMLElement>;
    @ViewChild('panelEl', { read: ElementRef }) panelEl?: ElementRef<HTMLElement>;
    @ViewChildren('optionEl', { read: ElementRef }) optionEls?: QueryList<ElementRef<HTMLElement>>;

    @Input() label?: string;
    @Input() placeholder = '';
    @Input() icon = 'icon_search';
    @Input() iconPosition: 'left' | 'right' = 'right';
    @Input() iconColor = '#9CA3AF';
    @Input() type: 'text' | 'search' = 'text';
    @Input() readOnly = false;
    @Input() error = false;
    @Input() block = true;
    @Input() size: 'sm' | 'md' | 'lg' = 'md';
    @Input() sizeIcon: 'sm' | 'md' | 'lg' = 'md';
    @Input() uiMode: 'form' | 'filter' = 'form';

    @Input() minChars = 2;
    @Input() debounceMs = 350;
    @Input() maxItems = 10;
    @Input() openOptionsOnFocus = false;

    @Input({ required: true }) searchFn!: (term: string) => Observable<T[]>;

    @Input() resolveById?: (id: string | number) => Observable<T | null>;

    @Input() displayWith: (item: T) => string = (i: any) => i?.label ?? i?.name ?? String(i);
    @Input() trackBy: (item: T) => string | number = (i: any) => i?.id;
    @Input() returnValue: 'id' | 'text' = 'id';

    @Output() selected = new EventEmitter<T>();

    displayValue = '';
    disabled = false;

    panelOpen = false;
    loading = false;
    results: T[] = [];
    activeIndex = -1;

    trackByFn = (_: number, item: T) => this.trackBy(item);

    private term$ = new Subject<{ value: string; force?: boolean }>();
    private destroy$ = new Subject<void>();

    private onChange: (v: any) => void = () => { };
    private onTouched: () => void = () => { };

    private cache = new Map<string, T>();
    private selectedItem: T | null = null;

    private ignoreNextDocDown = false;

    scrollStrategy: ScrollStrategy;
    positions: ConnectedPosition[] = [
        { originX: 'start', originY: 'bottom', overlayX: 'start', overlayY: 'top', offsetY: 4 },
        { originX: 'start', originY: 'top', overlayX: 'start', overlayY: 'bottom', offsetY: -4 },
    ];

    overlayWidth = 0;
    readonly listboxId = `core-ui-autocomplete-${AutocompleteFormComponent.nextId++}`;

    constructor(private elementRef: ElementRef, private overlay: Overlay) {
        this.scrollStrategy = this.overlay.scrollStrategies.reposition();
    }

    onPanelMouseDown(): void {
        this.ignoreNextDocDown = true;
        queueMicrotask(() => (this.ignoreNextDocDown = false));
    }

    onOptionMouseEnter(index: number): void {
        if (this.selectedKey !== null) {
            this.activeIndex = this.getSelectedResultIndex();
            return;
        }

        this.activeIndex = index;
    }

    @HostListener('document:mousedown', ['$event'])
    onDocMouseDown(ev: MouseEvent): void {
        if (!this.panelOpen) return;
        if (this.ignoreNextDocDown) return;
        const t = ev.target as Node;
        const host = this.elementRef.nativeElement as HTMLElement;
        const panel = this.panelEl?.nativeElement;
        const clickedInOrigin = host.contains(t);
        const clickedInPanel = !!panel && panel.contains(t);
        if (!clickedInOrigin && !clickedInPanel) {
            this.closePanel();
        }
    }

    onOverlayDetach(): void {
        if (this.panelOpen) this.closePanel();
    }

    private updateOverlayWidth(): void {
        const w = this.originRef?.nativeElement?.getBoundingClientRect().width ?? 0;
        if (w !== this.overlayWidth) {
            this.overlayWidth = w;
        }
    }

    ngOnInit(): void {
        this.term$
            .pipe(
                debounceTime(this.debounceMs),
                distinctUntilChanged((previous, current) => !current.force && previous.value === current.value),
                switchMap(({ value: raw }) => {
                    const term = (raw ?? '').trim();

                    if (!term) {
                        if (this.openOptionsOnFocus) {
                            this.prepareSearch();

                            return this.searchFn('').pipe(
                                catchError(() => of([] as T[])),
                                finalize(() => (this.loading = false))
                            );
                        }

                        this.loading = false;
                        this.results = [];
                        this.activeIndex = -1;
                        return of([] as T[]);
                    }

                    if (term.length < this.minChars) {
                        this.loading = false;
                        this.results = [];
                        this.activeIndex = -1;
                        return of([] as T[]);
                    }

                    this.prepareSearch();

                    return this.searchFn(term).pipe(
                        catchError(() => of([] as T[])),
                        finalize(() => (this.loading = false))
                    );
                }),
                takeUntil(this.destroy$)
            )
            .subscribe((items) => {
                this.results = (items ?? []).slice(0, this.maxItems);
                this.activeIndex = this.getInitialActiveIndex();
                this.scrollActiveOptionIntoView();
            });
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    writeValue(v: any): void {
        if (v === null || v === undefined || v === '') {
            this.selectedItem = null;
            this.displayValue = '';
            this.results = [];
            this.closePanel();
            return;
        }

        if (this.returnValue === 'text') {
            this.selectedItem = null;
            this.displayValue = String(v);
            return;
        }

        const key = String(v);

        const cached = this.cache.get(key);
        if (cached) {
            this.selectedItem = cached;
            this.displayValue = this.displayWith(cached);
            return;
        }

        if (!this.resolveById) {
            this.selectedItem = null;
            this.displayValue = '';
            return;
        }

        this.resolveById(v)
            .pipe(
                catchError(() => of(null)),
                takeUntil(this.destroy$)
            )
            .subscribe((item) => {
                this.selectedItem = item;
                this.displayValue = item ? this.displayWith(item) : '';

                if (item) {
                    const id = this.trackBy(item);
                    this.cache.set(String(id), item);
                }
            });
    }

    registerOnChange(fn: any): void { this.onChange = fn; }
    registerOnTouched(fn: any): void { this.onTouched = fn; }
    setDisabledState(isDisabled: boolean): void { this.disabled = isDisabled; }

    onTermChange(term: string): void {
        if (this.disabled || this.readOnly) return;

        this.displayValue = term;

        const clean = term.trim();
        this.panelOpen = this.openOptionsOnFocus || clean.length > 0;
        if (this.panelOpen) setTimeout(() => this.updateOverlayWidth(), 0);

        if (clean.length >= this.minChars || (this.openOptionsOnFocus && !clean)) {
            this.prepareSearch();
        }

        if (this.returnValue === 'text') {
            this.onChange(term);
        } else {
            this.onChange(null);
            this.selectedItem = null;
        }

        this.term$.next({
            value: term,
            force: this.openOptionsOnFocus && !clean
        });
    }

    onFocus(): void {
        if (this.disabled || this.readOnly) return;

        const clean = this.displayValue.trim();
        this.panelOpen = this.openOptionsOnFocus || clean.length > 0;
        if (this.panelOpen) setTimeout(() => this.updateOverlayWidth(), 0);

        if (this.selectedItem) {
            this.loading = false;
            this.results = [this.selectedItem];
            this.activeIndex = 0;
            return;
        }

        if (this.openOptionsOnFocus) {
            this.prepareSearch();
            this.term$.next({ value: this.displayValue, force: true });
            return;
        }

        if (clean.length >= this.minChars) {
            this.prepareSearch();
            this.term$.next({ value: this.displayValue, force: true });
        }
    }

    onBlur(): void {
        this.onTouched();
        setTimeout(() => this.closePanel(), 120);
    }

    onKeydown(e: KeyboardEvent): void {
        if (!this.panelOpen) return;

        const max = this.results.length;

        switch (e.key) {
            case 'ArrowDown':
                if (!max) return;
                e.preventDefault();
                this.scrollActiveOptionIntoView(this.moveActiveIndex(1));
                break;

            case 'ArrowUp':
                if (!max) return;
                e.preventDefault();
                this.scrollActiveOptionIntoView(this.moveActiveIndex(-1));
                break;

            case 'Enter':
                if (!max) return;
                e.preventDefault();
                if (this.activeIndex >= 0 && this.activeIndex < max) {
                    this.selectItem(this.results[this.activeIndex]);
                }
                break;

            case 'Escape':
                e.preventDefault();
                this.closePanel();
                break;
        }
    }

    selectItem(item: T): void {
        const id = this.trackBy(item);
        const key = String(id);

        this.cache.set(key, item);
        this.selectedItem = item;

        const text = this.displayWith(item);
        this.displayValue = text;

        this.onChange(this.returnValue === 'id' ? id : text);
        this.selected.emit(item);

        this.closePanel();
    }

    isSelected(item: T): boolean {
        const selectedKey = this.selectedKey;

        return selectedKey !== null && String(this.trackBy(item)) === String(selectedKey);
    }

    closePanel(): void {
        this.panelOpen = false;
        this.activeIndex = -1;
        this.loading = false;
    }

    private prepareSearch(): void {
        this.loading = true;
        this.results = [];
        this.activeIndex = -1;
    }

    private moveActiveIndex(direction: 1 | -1): ScrollLogicalPosition {
        const max = this.results.length;

        if (!max) return 'nearest';

        if (this.activeIndex < 0) {
            this.activeIndex = direction === 1 ? 0 : max - 1;
            return direction === 1 ? 'start' : 'end';
        }

        const currentIndex = this.activeIndex;
        const nextIndex = (currentIndex + direction + max) % max;
        const scrollBlock: ScrollLogicalPosition = direction === -1 && currentIndex === 0
            ? 'end'
            : direction === 1 && currentIndex === max - 1
                ? 'start'
                : 'nearest';

        this.activeIndex = nextIndex;
        return scrollBlock;
    }

    private getInitialActiveIndex(): number {
        if (!this.results.length) return -1;

        const selectedIndex = this.getSelectedResultIndex();

        return selectedIndex >= 0 ? selectedIndex : 0;
    }

    private getSelectedResultIndex(): number {
        const selectedKey = this.selectedKey;

        return selectedKey === null
            ? -1
            : this.results.findIndex(item => String(this.trackBy(item)) === String(selectedKey));
    }

    private scrollActiveOptionIntoView(block: ScrollLogicalPosition = 'nearest'): void {
        setTimeout(() => {
            requestAnimationFrame(() => {
                if (this.activeIndex < 0) return;

                const option = this.optionEls?.get(this.activeIndex)?.nativeElement;
                option?.scrollIntoView({ block });
            });
        }, 0);
    }


    get showMinCharsHint(): boolean {
        const n = this.displayValue.trim().length;
        return this.panelOpen && n > 0 && n < this.minChars && !this.loading;
    }

    get showNoResults(): boolean {
        const n = this.displayValue.trim().length;
        const canSearchEmpty = this.openOptionsOnFocus && n === 0;

        return this.panelOpen && (n >= this.minChars || canSearchEmpty) && !this.loading && this.results.length === 0;
    }

    get selectedKey(): string | number | null {
        return this.selectedItem ? this.trackBy(this.selectedItem) : null;
    }

    get activeOptionId(): string | null {
        return this.activeIndex >= 0 ? this.getOptionId(this.activeIndex) : null;
    }

    getOptionId(index: number): string {
        return `${this.listboxId}-option-${index}`;
    }
}
